import * as React from 'react';
import { Observer, observer } from 'mobx-react-lite';
import { Layout, Table, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import { Utils } from '../../../common/services/Utils';
import { OrderBy } from '../../../common/types';
import { AdministrationHistoryStore } from '../../stores';
import { HistoryRecordExpandedRow } from '..';
import BaseHistoryRecordModel from '../../models/BaseHistoryRecordModel';
import LayoutHeader from '../../../../components/LayoutHeader';
import AdministrationHistoryFilters from './AdministrationHistoryFilters';

interface Props {
    store: AdministrationHistoryStore;
}

const AdministrationHistoryGrid: React.FC<Props> = ({ store }) => {
    React.useEffect(() => {
        if (!store.isLoadingUsers) {
            store.getGroups().then(() => store.searchHistory());
        }
    }, [store, store.isLoadingUsers]);

    React.useEffect(() => {
        return () => store.dispose();
    }, [store]);

    const getSorterOrder = React.useCallback(
        (columnKey: string) => {
            if (!store.orderBy || store.orderBy.field !== columnKey) {
                return;
            }

            return store.orderBy.direction === 'ASC' ? 'ascend' : 'descend';
        },
        [store.orderBy]
    );

    const columns: ColumnsType<BaseHistoryRecordModel> = React.useMemo(() => {
        return [
            {
                title: 'Data Object',
                dataIndex: 'objectLabel',
                key: 'objectLabel',
                width: '20%'
            },
            {
                title: 'Entity Name',
                dataIndex: 'entityName',
                key: 'entityName',
                width: '25%',
                render: (_: unknown, record: BaseHistoryRecordModel) => (
                    <Observer>{() => <span>{record.displayName}</span>}</Observer>
                )
            },
            {
                title: 'Action',
                dataIndex: 'action',
                key: 'action',
                width: '10%'
            },
            {
                title: 'Username',
                dataIndex: 'userName',
                key: 'userName',
                width: '25%'
            },
            {
                title: 'Date',
                dataIndex: 'createDate',
                key: 'createDate',
                width: '20%',
                sorter: true,
                sortOrder: getSorterOrder('createDate'),
                render: (createDate: string) => {
                    return {
                        children: Utils.formatDateStringShort(createDate)
                    };
                }
            }
        ];
    }, [getSorterOrder]);

    const pagination: TablePaginationConfig = {
        pageSize: AdministrationHistoryStore.HISTORY_PAGE_SIZE,
        total: store.total,
        current: store.page + 1,
        showSizeChanger: false,
        onChange: (page: number) => store.setPage(page - 1)
    };

    const onChange = (sorter: SorterResult<BaseHistoryRecordModel>) => {
        let orderBy: OrderBy | undefined;

        if (sorter.columnKey && sorter.order) {
            orderBy = {
                field: sorter.columnKey as string,
                direction: sorter.order === 'ascend' ? 'ASC' : 'DESC'
            };
        }

        store.setOrderBy(orderBy);
        store.searchHistory();
    };

    return (
        <Layout>
            <LayoutHeader
                title="Change History"
                helpMessage="Chronological tracking of administration modifications for accountability and troubleshooting"
                helpMessageTooltipPlacement="leftBottom"
                buttons={[
                    <Button
                        key="clearFilters"
                        onClick={() => {
                            store.clearFilters();
                            store.performSearch();
                        }}
                        size="large"
                        className="light"
                    >
                        Clear filters
                    </Button>
                ]}
            />
            <Layout.Content>
                <AdministrationHistoryFilters store={store} />
                <Table
                    data-id="administration-history-table"
                    scroll={{ y: 'calc(100vh - 300px)' }}
                    rowKey={r => r.id}
                    dataSource={store.isLoadingUsers || store.isLoadingGroups ? [] : store.historyRecords}
                    columns={columns}
                    loading={store.isLoading || store.isLoadingUsers || store.isLoadingGroups}
                    pagination={pagination}
                    onChange={(_, __, sorter: SorterResult<BaseHistoryRecordModel>) => onChange(sorter)}
                    expandable={{
                        expandRowByClick: true,
                        expandedRowRender: record => (
                            <Observer>
                                {() => (
                                    <HistoryRecordExpandedRow
                                        fields={record.fields}
                                        diffSplitViewEnabled={store.diffSplitViewEnabled}
                                        toggleDiffSplitView={store.toggleDiffSplitView}
                                    />
                                )}
                            </Observer>
                        ),
                        expandIcon: ({ expanded, onExpand, record }) => (
                            <i
                                className="alpha-icon xs expand-row arrow-expand"
                                style={
                                    expanded
                                        ? {
                                              transform: 'rotate(90deg)',
                                              marginLeft: 0
                                          }
                                        : {}
                                }
                                onClick={e => onExpand(record, e)}
                            />
                        )
                    }}
                />
            </Layout.Content>
        </Layout>
    );
};

export default observer(AdministrationHistoryGrid);
